import { ComponentProps } from "react";
import ReactDatePicker from "react-datepicker";
import { endOfMonth, format, startOfDay, startOfMonth } from "date-fns";
import { Text } from "../../Atoms/Typography";
import { GrFormPrevious, GrFormNext } from "react-icons/gr";
import style from "./LoadingCalendar.module.css";
import { addMonths, subMonths } from "date-fns";
import Loader from "../../Atoms/Loader";
import classNames from "classnames";
import useAvailability from "../../../hooks/useAvailability";

type LoadingCalendarProps = {
  from: Date;
  until: Date;
} & ComponentProps<typeof ReactDatePicker> &
  Pick<ReturnType<typeof useAvailability>, "loading" | "loadedRange">;

export default function LoadingCalendar({
  from,
  until,
  loadedRange,
  loading,
  ...datePickerProps
}: LoadingCalendarProps) {
  const startOfDayFrom = startOfDay(from);
  const startOfDayUntil = startOfDay(until);

  return (
    <ReactDatePicker
      renderCustomHeader={({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => {
        const nextMonth = startOfMonth(addMonths(date, 1));

        const nextMonthIsLoading =
          loading &&
          startOfDayUntil.getTime() > loadedRange.until.getTime() &&
          nextMonth.getTime() > loadedRange.until.getTime();

        const previousMonth = endOfMonth(subMonths(date, 1));
        const previousMonthIsLoading =
          loading &&
          startOfDayFrom.getTime() < loadedRange.from.getTime() &&
          previousMonth.getTime() < loadedRange.from.getTime();

        return (
          <div className={style.Header}>
            {previousMonthIsLoading ? (
              <div className={style.Loader}>
                <Loader size={20} thickness={4} />
              </div>
            ) : (
              <button
                className={classNames({
                  [style.Button]: true,
                  [style.Disabled]: prevMonthButtonDisabled,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  decreaseMonth();
                }}
                disabled={prevMonthButtonDisabled}
              >
                <GrFormPrevious />
              </button>
            )}

            <Text bold size="medium">
              {format(date, "MMMM yyyy")}
            </Text>

            {nextMonthIsLoading ? (
              <div className={style.Loader}>
                <Loader size={20} thickness={4} />
              </div>
            ) : (
              <button
                className={classNames({
                  [style.Button]: true,
                  [style.Disabled]: nextMonthButtonDisabled,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  increaseMonth();
                }}
                disabled={nextMonthButtonDisabled}
              >
                <GrFormNext />
              </button>
            )}
          </div>
        );
      }}
      {...datePickerProps}
    />
  );
}
